import type { BrokerGuide } from "./brokerGuides";

// Portuguese (pt-PT) broker guides: same keys, steps, images and order as the
// English ones in brokerGuides.ts (checked by src/data/contentTranslations.test.ts).
// The screenshots show the broker apps in English, so the broker's own button and menu
// names stay in English.
export const BROKER_GUIDES_PT_PT: Record<string, BrokerGuide> = {
  t212: {
    brokerName: "Trading 212",
    intro:
      "Exporta todo o teu histórico de transações a partir da app móvel do T212 em três toques.",
    steps: [
      {
        title: 'Abre o "History"',
        description:
          'Abre a app do Trading 212 e toca no ícone no canto inferior direito. Depois seleciona "History".',
        image: "/guides/t212/Get_CSVs/step-1.jpg",
      },
      {
        title: "Toca no ícone de exportação",
        description:
          'No canto superior direito do ecrã "History", toca no ícone de exportação.',
        image: "/guides/t212/Get_CSVs/step-2.jpg",
      },
      {
        title: "Seleciona o período e exporta",
        description:
          'O T212 exporta até 1 ano por ficheiro. Seleciona todos os dados que queres exportar. Toca em "Export" e o CSV será transferido para o teu dispositivo. Repete para cada ano, se for preciso.',
        image: "/guides/t212/Get_CSVs/step-3.jpg",
      },
    ],
    note: "O T212 limita as exportações a 1 ano por ficheiro. Se o teu histórico abrange vários anos, exporta um ficheiro por ano e seleciona-os todos de uma vez na janela de importação da Oitava.",
  },
  ibkr: {
    brokerName: "Interactive Brokers",
    intro:
      "Transfere o teu extrato de atividade a partir do Client Portal da IBKR.",
    steps: [
      {
        title: "Inicia sessão no Client Portal",
        description:
          "Vai a clientportal.ibkr.com e inicia sessão na tua conta.",
      },
      {
        title: "Abre os relatórios",
        description: 'Navega até "Performance & Reports" → "Statements".',
      },
      {
        title: "Gera um extrato de atividade",
        description:
          'Seleciona "Activity" como tipo de extrato, define o período para cobrir todo o teu histórico e escolhe CSV como formato.',
      },
      {
        title: "Transfere e importa",
        description:
          'Clica em "Run" e transfere o ficheiro. Depois carrega-o na janela de importação da Oitava.',
      },
    ],
  },
  degiro: {
    brokerName: "DEGIRO",
    intro:
      'Exporta a tua atividade a partir da plataforma web da DEGIRO. Usa o extrato da conta ("Account Statement") e não a exportação de transações ("Transactions").',
    steps: [
      {
        title: 'Vai a "Inbox" > "Account Statement"',
        description:
          'Abre a "Inbox" (canto inferior direito) e seleciona "Account Statement". Este é o ficheiro correto, e não o separador "Transactions", que só contém compras e vendas e não inclui dividendos, comissões nem juros.',
        image: "/guides/degiro/step_1.jpg",
      },
      {
        title: "Seleciona o período e exporta como CSV",
        description:
          'Escolhe o período que cobre as transações que queres importar. Clica em "Export". Se tiveres dúvidas, seleciona todo o teu histórico. A Oitava deteta e ignora duplicados automaticamente, por isso importações sobrepostas são seguras.',
        image: "/guides/degiro/step_2.jpg",
      },
      {
        title: "Carrega na Oitava",
        description:
          "Escolhe CSV, transfere o ficheiro e depois usa o botão Importar na página Transações para o carregar.",
        image: "/guides/degiro/step_3.jpg",
      },
    ],
  },
  robinhood: {
    brokerName: "Robinhood",
    intro:
      "Gera um relatório de atividade da conta na app da Robinhood e transfere-o em CSV.",
    steps: [
      {
        title: "Abre o menu da conta",
        description:
          'Na app da Robinhood, toca no ícone da conta no canto inferior direito (1) e depois em "Reports and statements" (2).',
        image: "/guides/Robinhood/step_1.jpg",
      },
      {
        title: 'Abre os "Account activity reports"',
        description:
          'Toca em "Account activity reports" e depois em "Reports" (3). Esta é a exportação em CSV. Os "Monthly statements" por baixo são PDF, que a Oitava não consegue ler.',
        image: "/guides/Robinhood/step_2.jpg",
      },
      {
        title: "Inicia um novo relatório",
        description:
          'Os relatórios que já geraste aparecem listados aqui. Toca em "Generate new report" (4).',
        image: "/guides/Robinhood/step_3.jpg",
      },
      {
        title: "Define o período",
        description:
          'Introduz uma data de início e uma data de fim que cubram todo o teu histórico (5) e toca em "Generate report" (6). Define a data de início para antes do teu primeiro depósito, caso contrário faltarão as transações mais antigas.',
        image: "/guides/Robinhood/step_4.jpg",
      },
      {
        title: "Transfere e importa",
        description:
          "O relatório demora um pouco a ser preparado e a Robinhood avisa-te quando estiver pronto. Toca no ícone de transferência ao lado dele e depois carrega o CSV na janela de importação da Oitava.",
        image: "/guides/Robinhood/step_5.jpg",
      },
    ],
    note: "O suporte à Robinhood Europe está em beta. O relatório de atividade da conta cobre depósitos, compras e vendas de criptomoedas, transferências, rendimentos de staking e recompensas em criptomoedas. Não inclui os pagamentos de rendimento sobre o teu saldo em numerário: a Robinhood só os coloca nos extratos mensais em PDF, um ficheiro por mês, por isso adiciona-os tu como transações de juros na Oitava.",
  },
  xtb: {
    brokerName: "XTB",
    intro:
      "Exporta o teu histórico de transações a partir da app da XTB, cobrindo todas as contas em moedas diferentes que tenhas.",
    steps: [
      {
        title: "Abre o menu da conta",
        description:
          "Na app da XTB, toca no ícone de perfil no canto superior esquerdo.",
        image: "/guides/XTB/step_1.png",
      },
      {
        title: 'Abre "All operations history"',
        description: 'Desce e toca em "All operations history".',
        image: "/guides/XTB/step_2.png",
      },
      {
        title: 'Muda para "Cash operations" e exporta',
        description:
          'Toca no separador "Cash operations" e depois no ícone de transferência no canto superior direito.',
        image: "/guides/XTB/step_3.png",
      },
      {
        title: "Inicia um novo relatório",
        description: 'Toca em "New report".',
        image: "/guides/XTB/step_4.png",
      },
      {
        title: "Seleciona o período completo e todas as contas",
        description:
          'Define o período como "All" para cobrir todo o teu histórico. Em "Accounts", seleciona todas as contas listadas e não apenas a que aparece por omissão. A XTB dá a cada moeda (EUR, USD, etc.) a sua própria conta, e se selecionares só uma, as transações das outras não estarão na exportação. Depois toca em "Generate report".',
        image: "/guides/XTB/step_5.png",
      },
      {
        title: "Transfere e importa",
        description:
          'Toca em "Download report". Se selecionaste mais do que uma conta, a XTB transfere um ficheiro separado para cada uma, por exemplo um para a conta em EUR e outro para a conta em USD. Importa-os todos para uma só carteira XTB na Oitava, ou cada um para a sua própria carteira. A nota abaixo explica as duas opções.',
        image: "/guides/XTB/step_6.png",
      },
    ],
    note: 'A XTB dá a cada moeda (EUR, USD, etc.) a sua própria conta e também permite abrir contas adicionais, por exemplo uma por cada membro da família. Cada conta que selecionares vem num ficheiro próprio. Os novos depósitos entram sempre na tua primeira conta, e o dinheiro que passas para as outras aparece na XTB como uma transferência. A Oitava importa cada transferência como um levantamento da conta de onde saiu e um depósito na conta para onde foi, incluindo as transferências que convertem moeda.\n\nUma carteira para todas as tuas contas XTB: importa todos os ficheiros para a mesma carteira XTB. Ficas com uma vista única de todas as tuas posições na XTB, com o numerário separado por moeda. As transferências entre as tuas contas anulam-se, por isso o numerário e o capital investido ficam corretos, mas continuam a aparecer no histórico de depósitos e levantamentos desta carteira.\n\nUma carteira por conta XTB: cria uma carteira para cada conta e importa cada ficheiro para a sua carteira. Cada carteira mostra apenas as posições e o numerário dessa conta. A tua primeira conta mostrará um levantamento sempre que passares dinheiro para outra conta, e a carteira que o recebe mostra-o como um depósito. A tua rentabilidade não é afetada, e a Carteira agregada volta a somá-las.\n\nDe qualquer forma, seleciona todas as contas na lista "Accounts" antes de gerar o relatório. Se deixares uma de fora, o dinheiro passado para ela vai parecer que saiu da XTB.',
  },
};

export const T212_API_GUIDE_PT_PT: BrokerGuide = {
  brokerName: "Trading 212",
  intro:
    "Gera um Personal Access Token na app do Trading 212 em quatro passos.",
  steps: [
    {
      title: "Abre o menu da conta",
      description:
        'Na app do Trading 212, toca no ícone no canto inferior direito e depois em "Settings".',
      image: "/guides/t212/Get_API_KEY/step1.jpg",
    },
    {
      title: "Vai à secção da API",
      description:
        'Desce e toca em "API (Personal Access)". Pode ser preciso ativá-la primeiro, se ainda não o fizeste.',
      image: "/guides/t212/Get_API_KEY/step2.jpg",
    },
    {
      title: 'Toca em "Generate API key"',
      description:
        'No ecrã "API (Beta)", toca em "Generate API key" para abrir o ecrã "New API key".',
      image: "/guides/t212/Get_API_KEY/step3.jpg",
    },
    {
      title: "Seleciona as permissões necessárias",
      description:
        'Dá um nome à chave e depois ativa "History", "History - Dividends", "History - Orders", "History - Transactions" e "Metadata". Deixa as restantes desativadas. A Oitava só lê o teu histórico, nunca faz ordens nem mexe nas tuas Pies. Toca em "Generate key".',
      image: "/guides/t212/Get_API_KEY/step4.jpeg",
    },
  ],
  note: "Copia o ID da chave da API e a chave secreta que aparecem depois de gerar. Vais precisar das duas, e a chave secreta só é mostrada uma vez. Usa a chave Live para carteiras com dinheiro real e a chave Demo para simulação.",
};

export const IB_API_GUIDE_PT_PT: BrokerGuide = {
  brokerName: "Interactive Brokers",
  wideImages: true,
  intro:
    "A sincronização com a IBKR precisa de duas coisas do Client Portal: uma Activity Flex Query que diz quais os dados a enviar e um token do Flex Web Service que permite à Oitava pedi-los. Configuras isto uma só vez e, depois disso, a sincronização é automática e não precisas de voltar aqui.",
  steps: [
    {
      title: "Abre as definições do Flex Web Service",
      description:
        'No Client Portal, vai a "Performance & Reports" → "Flex Queries". À direita, no painel "Flex Web Service Configuration": (1) clica no ícone da roda dentada.',
      image: "/guides/ibkr/1_flexqueries.png",
    },
    {
      title: "Ativa o serviço e gera o teu token",
      description:
        '(1) Marca "Flex Web Service Status". (2) Clica em "Generate New Token". Aparece um menu "Expire after": escolhe 1 ano, porque a opção por omissão de 6 horas faria a sincronização deixar de funcionar no próprio dia. (3) Clica em "Save". (4) Copia o "Current Token".\n\nDeixa "Valid for IP Address" vazio.',
      image: "/guides/ibkr/2_configure_flex_web_service.png",
    },
    {
      title: "Inicia o construtor de consultas com IA",
      description:
        'Volta a "Flex Queries". No painel "Configure with AI" no topo: (1) clica em "Configure with AI".',
      image: "/guides/ibkr/3_connfigure_with_AI.png",
    },
    {
      title: "Cola este pedido",
      description:
        '(1) Cola o seguinte na caixa de descrição e depois (2) clica em "Generate Flex Query":\n\n"Activity Flex Query, XML, period Last 365 Calendar Days. Sections: Trades, Cash Transactions, Corporate Actions, Financial Instrument Information, Include Currency Rates. Select all fields in each"\n\nA IBKR limita esta caixa a 200 caracteres, por isso o pedido está escrito para caber. Mantém-no em inglês, tal como está.',
      image: "/guides/ibkr/3_generate_query_AI.png",
    },
    {
      title: 'Ativa "Include Currency Rates"',
      description:
        'A IA ignora esta opção, por isso tem de ser definida à mão. No ecrã "Review Flex Query": (1) clica em "Edit Manually", (2) procura "Include Currency Rates?" em "General Configuration" e define como "Yes", (3) clica em "Save".\n\nIsto permite que a Oitava use as taxas de câmbio da própria IBKR nas tuas transações, em vez de as estimar.',
      image: "/guides/ibkr/4_include_currency_rates.png",
    },
    {
      title: "Copia o Query ID",
      description:
        'No topo de "Review Flex Query": (1) copia o "Query ID", um número de 7 dígitos. Não o confundas com o número da tua conta, que começa por U.',
      image: "/guides/ibkr/5_copy_query_id.png",
    },
    {
      title: "Cola ambos na Oitava",
      description:
        "Introduz o token e o Query ID nas definições de sincronização da tua carteira. Ambos são encriptados antes de serem guardados e o token é só de leitura. Consegue obter extratos, mas não consegue negociar nem movimentar dinheiro.",
    },
  ],
  note: 'Preferes construir a consulta tu mesmo, em vez de usar a IA? Clica no + ao lado de "Activity Flex Query" e marca cinco secções, "Trades", "Cash Transactions", "Corporate Actions", "Financial Instrument Information" e "Include Currency Rates", clicando em "Select All" dentro de cada uma. Não precisas de escolher campos individuais. "Cash Transactions" é a que as pessoas esquecem: contém os teus dividendos, depósitos, levantamentos, comissões e juros, e sem ela a Oitava vê as tuas operações mas nada do teu numerário. "Corporate Actions" permite à Oitava atribuir à empresa certa o dinheiro de um direito de dividendo, que a IBKR regista com o nome do próprio direito.\n\nAlguns limites que convém conhecer. A IBKR só disponibiliza através do Flex os últimos quatro anos civis mais o ano atual, por isso, se a tua conta for mais antiga, sincroniza o que for possível e importa um extrato de atividade em CSV para os anos anteriores. Cada pedido está limitado a 365 dias, por isso históricos mais longos são obtidos em lotes. Os dados do Flex chegam até ao dia útil anterior, ou seja, as operações de hoje aparecem amanhã, e as contas novas que aguardam o primeiro financiamento ainda não conseguem gerar extratos, por isso dá-lhe um dia depois de abrires a conta. Gerar um novo token invalida o anterior, por isso, se o voltares a gerar mais tarde, atualiza-o também na Oitava.',
};
